import {makeAutoObservable} from "mobx";
import {api, calculateLatestVhsDocuments, catchApi, toSortedVhsEvents, VhsDocInfo} from "../../api";
import {CustomerProjectionDto, DocumentTypeDto, EventTypeDto, SourceSystemDto} from "../../api/vhs";

export type DocType = DocumentTypeDto

export interface VhsEvent {
    id: string
    system: SourceSystemDto
    systemId: string
    vin: string
    username: string
    customerId: string
    createdDate: Date
    systemEventType: EventTypeDto
    lastScan?: Date
    documentId?: string
    documentType?: DocType
    documentLocation?: string
    corNumber?: string
    licensePlate?: string
}

interface VhsOverviewData {
    vin: string
    customer?: CustomerProjectionDto
    documents: VhsDocInfo[]
    trackingLink?: string
}

function calculateOverviewData(data: VhsEvent[], customer: CustomerProjectionDto | undefined): VhsOverviewData {
    const vin = data[0].vin


    const trackingLink = data.filter(e => e.system === 'FMS')
        .filter(e => e.systemEventType === EventTypeDto.SHIPMENT_CLOSED)
        .map(e => e.corNumber)
        .find(e => !e)

    const documents = calculateLatestVhsDocuments(data)

    return {
        vin,
        customer,
        trackingLink,
        documents
    }
}


function mapSystem(input: SourceSystemDto): string {
    switch (input) {
        case SourceSystemDto.SEA:
            return 'Scanimport'
        case SourceSystemDto.VDA:
            return 'Fahrzeugerfassung'
        case SourceSystemDto.VRS:
            return 'Fahrzeugarchiv'
        case SourceSystemDto.SLS:
            return 'Einlagerung'
        case SourceSystemDto.FMS:
            return 'Versand'
        case SourceSystemDto.PMS:
            return 'Druck'
    }
}

class VhsDetailStore {
    get customerId(): string | undefined {
        return this._customerId;
    }

    set customerId(value: string | undefined) {
        this._customerId = value;
    }
    get customer(): CustomerProjectionDto | undefined {
        return this._customer;
    }

    set customer(value: CustomerProjectionDto | undefined) {
        this._customer = value;
    }
    get overviewData(): VhsOverviewData | undefined {
        return this._overviewData;
    }

    set overviewData(value: VhsOverviewData | undefined) {
        this._overviewData = value;
    }
    private get excludedIds(): Map<string, boolean> {
        return this._excludedIds;
    }

    private set excludedIds(value: Map<string, boolean>) {
        this._excludedIds = value;
    }

    get data(): VhsEvent[] {
        return this._data;
    }

    set data(value: VhsEvent[]) {
        this._data = value;
    }

    get vin(): string | undefined {
        return this._vin;
    }

    set vin(value: string | undefined) {
        this._vin = value;
    }

    private _vin: string | undefined
    private _customerId: string | undefined
    private _data: VhsEvent[] = []
    private _overviewData: VhsOverviewData | undefined
    private _customer: CustomerProjectionDto | undefined
    private _excludedIds: Map<string, boolean> = new Map<string, boolean>()

    constructor() {
        makeAutoObservable(this)
    }

    isExcluded(id: string) {
        return this.excludedIds.get(id) || false
    }

    updateExcluded(id: string, excluded: boolean) {
        this.excludedIds.set(id, excluded)
    }

    async load(vin: string, customerId: string): Promise<any> {
        this.vin = vin
        this.customerId = customerId
        this.excludedIds = new Map<string, boolean>()
        await this.reload()
        return vin
    }

    async reload() {
        const {vin, customerId} = this
        if (!vin || !customerId) {
            return
        }
        await this.loadCustomerInfo(customerId)

        const response = await catchApi(async () => api.vhs.api.getHistory(customerId, vin))

        const responseData = response?.data
        if (responseData) {
            this.data = toSortedVhsEvents(responseData)
        } else {
            this.data = []
        }

        this.recalculateOverview()
    }

    recalculateOverview() {
        const data = this.data.filter(e => !this.excludedIds.get(e.id))
        this.overviewData = data.length ? calculateOverviewData(data, this.customer) : undefined
    }

    private async loadCustomerInfo(customerId: string) {
        const response = await catchApi(async () => await api.vhs.api.getCustomer(customerId))
        this.customer = response?.data
    }
}

const vhsDetailStore = new VhsDetailStore()

export {
    vhsDetailStore,
    mapSystem
}
